// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { endpoints } from '../../api/endpoints';
import { RootState } from '../index';

const DEBUG = false;

type Status = {
  id: string;
  cms_details: {
    public_name: any;
    reward_title: any;
    reward_subtitle: any;
    counter_measure_units: any;
  };
  start_date: string;
  end_date: string;
  status: string;
  art_bundle: any;
  collected: boolean;
  collecting: boolean;
  counter_value: number;
  game_id?: number;
};

type FetchAirTimerStatus = {
  data: Status | null;
  success: boolean;
  auth_type: string;
};

type FetchAirTimerStatusError = {
  rejectValue: {
    error: string;
  };
};

/*
{
  id: '552a6f5c-7a1a-4768-8b74-5af94c674bad',
  cms_details: {
    public_name: { en: 'FREE SPIN MANIA' },
    reward_title: { en: 'Unlimited free spins' },
    reward_subtitle: { en: 'for 10 minutes' },
    counter_measure_units: { en: 'Free spins' },
  },
  start_date: '2025-05-08T08:30:00.000Z',
  end_date: '2025-05-08T15:00:00.000Z',
  status: 'active',
  art_bundle: [],
  collected: true,
  counter_value: 845501,
};
*/

export const fetchStatus = createAsyncThunk<FetchAirTimerStatus, void, FetchAirTimerStatusError>(
  'airTimer/status',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      if (state.authentication?.access_token === null) {
        return rejectWithValue({
          error: "Couldn't fetch air timer status",
        });
      }

      const response = await axios.get(endpoints.airTimer.status, {
        headers: {
          Authorization: 'Bearer ' + state.authentication.access_token,
        },
      });

      if (response) {
        const data = response.data;
        return { data: data, success: true, auth_type: state.authentication.auth_type };
      }

      return rejectWithValue({
        error: "Couldn't fetch air timer status",
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

type ActivateProps = {
  id: string;
};

export const activate = createAsyncThunk<FetchAirTimerStatus, ActivateProps, FetchAirTimerStatusError>(
  'airTimer/activate',
  async ({ id }, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      const response = await axios.get(endpoints.airTimer.activate + '/' + id, {
        headers: {
          Authorization: 'Bearer ' + state.authentication.access_token,
        },
      });

      if (response) {
        const data = response.data;
        return { data: data, success: true, auth_type: state.authentication.auth_type };
      }

      return rejectWithValue({
        error: "Couldn't activate air timer",
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

export interface AirTimerState {
  status: Status | null;
  loaded: boolean;
  loading: boolean;
  auth_type: string | null;
  error: any;
}

export const airTimerSlice = createSlice({
  name: 'airTimer',
  initialState: <AirTimerState>{
    status: null,
    loaded: false,
    loading: false,
    error: null,
    auth_type: null,
  },
  reducers: {
    updateAirTimerStatus: (state, action) => {
      if (action.payload.counter_value != null) {
        if (state.status) state.status.counter_value = action.payload.counter_value;
      }
    },
    clearAirTimerStatus: (state) => {
      state.status = null;
      state.loaded = false;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStatus.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.success) {
          state.status = action.payload.data ?? null;
          state.auth_type = action.payload.auth_type;
          state.loaded = true;
        }
        DEBUG && console.log('[AirTimer] fetchStatus.fulfilled', action.payload);
      })
      .addCase(fetchStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        DEBUG && console.log('[AirTimer] fetchStatus.rejected', action.payload);
      })
      .addCase(activate.fulfilled, (state, action) => {
        if (action.payload.success) {
          if (state.status) {
            state.status.collected = true;
            state.status.collecting = false;
          }
        }
        DEBUG && console.log('[AirTimer] activate.fulfilled', action);
      })
      .addCase(activate.pending, (state, ...rest) => {
        if (state.status) state.status.collecting = true;
        DEBUG && console.log('[AirTimer] activate.pending', rest);
      })
      .addCase(activate.rejected, (state, action) => {
        if (state.status) {
          state.status.collecting = false;
        }
        state.error = action.payload?.error;
        DEBUG && console.log('[AirTimer] activate.rejected', action.payload);
      });
  },
});

export const { updateAirTimerStatus, clearAirTimerStatus } = airTimerSlice.actions;

export default airTimerSlice.reducer;
